import React, { useState } from 'react';
import { Button, HStack, Text, useToast, VStack } from '@chakra-ui/react';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { route } from '@php-beam/packages/frontend/src/hooks/useRouter';

export default function SubmissionAction({
  leadId,
  quoteId,
  onSubmissionCreated,
}: {
  leadId: number | null;
  quoteId: number | null;
  onSubmissionCreated?: (submissionId: number) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [skipDocs, setSkipDocs] = useState(true);
  const toast = useToast();

  const submit = async () => {
    if (!leadId || !quoteId) return;
    setSubmitting(true);
    try {
      await client.post(
        route('leads.quotes.submit', {
          lead: leadId,
          lead_quote: quoteId,
        }),
        {
          additional_files: [],
          note: 'DevTools submission',
          enable_fast_track: false,
          skip_document_selection: skipDocs,
        },
      );

      // Fetch the quote to get the latest submission ID
      const quoteRes = await client.get(
        route('leads.quotes.index', { lead: leadId }),
      );
      const quotes = quoteRes.data.quotes ?? [];
      const quote = quotes.find((q: any) => q.id === quoteId);
      const submissionId = quote?.submissions?.[quote.submissions.length - 1]?.id;

      if (submissionId && onSubmissionCreated) {
        onSubmissionCreated(submissionId);
      }

      toast({
        title: `Quote #${quoteId} submitted to UW${submissionId ? ` (Submission #${submissionId})` : ''}`,
        status: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const message =
        err?.response?.data?.message ||
        (errors ? Object.values(errors).flat().join(', ') : 'Unknown error');
      toast({ title: 'Submission failed', description: message, status: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack align="start" spacing={2}>
      <HStack spacing={3}>
        <Button
          colorScheme="purple"
          size="sm"
          onClick={submit}
          isLoading={submitting}
          isDisabled={!leadId || !quoteId}
        >
          Submit Quote #{quoteId} to UW
        </Button>
      </HStack>
      <HStack>
        <input
          type="checkbox"
          checked={skipDocs}
          onChange={(e) => setSkipDocs(e.target.checked)}
        />
        <Text fontSize="xs" color="gray.500">
          Skip document selection (requires Whiteglove/Enterprise tier)
        </Text>
      </HStack>
    </VStack>
  );
}
